import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { StatusPill } from '@/components/ui/StatusPill'
import { Tabs } from '@/components/ui/Tabs'
import type { RecruiterJob } from '@/features/recruiter/api/jobs.api'
import { useRecruiterJobsQuery } from '@/features/recruiter/hooks/useJobs'
import { formatDate, humanizeEnum } from '@/utils/format'

const STATUS_TABS = [
  { key: '', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'published', label: 'Published' },
  { key: 'paused', label: 'Paused' },
  { key: 'closed', label: 'Closed' },
  { key: 'archived', label: 'Archived' },
]

export function RecruiterJobsListPage() {
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const { data, isPending } = useRecruiterJobsQuery({ status: status || undefined, page, per_page: 10 })
  const navigate = useNavigate()

  const columns: DataTableColumn<RecruiterJob>[] = [
    {
      key: 'title',
      header: 'Job Title',
      render: (row) => <Link to={`/recruiter/jobs/${row.id}/edit`}>{row.title}</Link>,
    },
    {
      key: 'employmentType',
      header: 'Type',
      render: (row) => humanizeEnum(row.employmentType),
    },
    { key: 'workMode', header: 'Mode', render: (row) => humanizeEnum(row.workMode) },
    { key: 'status', header: 'Status', render: (row) => <StatusPill status={row.status} /> },
    {
      key: 'applicantCount',
      header: 'Applicants',
      render: (row) =>
        row.applicantCount > 0 ? (
          <Link
            to={`/recruiter/jobs/${row.id}/applicants`}
            className="badge bg-primary-transparent text-primary fs-12 text-decoration-none"
          >
            <i className="ti ti-users me-1" />{row.applicantCount}
          </Link>
        ) : (
          <span className="fs-12 text-muted">—</span>
        ),
    },
    { key: 'createdAt', header: 'Created', render: (row) => formatDate(row.createdAt) },
    {
      key: 'viewApplicants',
      header: '',
      render: (row) =>
        row.status === 'draft' ? null : (
          <Link to={`/recruiter/jobs/${row.id}/applicants`} className="btn btn-sm btn-primary fs-13">
            <i className="ti ti-users me-1" />View Applicants
          </Link>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <Link to={`/recruiter/jobs/${row.id}/edit`} className="btn btn-sm btn-light">
          <i className="ti ti-edit fs-13" />
        </Link>
      ),
    },
  ]

  return (
    <>
      <div className="my-4 d-flex align-items-center justify-content-between flex-wrap gap-2 page-header-breadcrumb">
        <div>
          <p className="fw-semibold fs-20 mb-0">Job Postings</p>
        </div>
        <Button onClick={() => navigate('/recruiter/jobs/new')}>Post a Job</Button>
      </div>

      <Tabs
        items={STATUS_TABS}
        activeKey={status}
        onChange={(key) => {
          setStatus(key)
          setPage(1)
        }}
      />

      <div className="card custom-card">
        <DataTable
          columns={columns}
          rows={data?.jobs ?? []}
          rowKey={(row) => row.id}
          loading={isPending}
          emptyMessage="No jobs posted yet."
          pagination={data?.meta}
          onPageChange={setPage}
        />
      </div>
    </>
  )
}
